/**
 * Pure factory for the OIDC (react-oidc-context / oidc-client-ts) configuration.
 *
 * Extracted from main.tsx so the security-critical settings are unit-testable
 * WITHOUT bootstrapping React or mocking the OIDC layer (M5, ARCHITECTURE §7):
 *  - Authorization Code + PKCE, public client — NO client secret.
 *  - Storage: sessionStorage (cleared on tab close) — NEVER localStorage
 *    (persistent cross-tab token storage is a security regression; PRD M5
 *    "no insecure persistence").
 *  - automaticSilentRenew uses the refresh token (offline_access scope).
 *  - onSigninCallback strips the OIDC query params (code/state/session_state)
 *    from the URL after the code exchange so they don't linger in history.
 */

import { WebStorageStateStore } from "oidc-client-ts";
import type { UserManagerSettings } from "oidc-client-ts";

import { config as appConfig } from "./config";

/**
 * The settings-based shape react-oidc-context's `AuthProvider` accepts: the
 * oidc-client-ts `UserManagerSettings` plus the provider's `onSigninCallback`
 * hook. (We use this rather than `AuthProviderProps`, which is a union whose
 * pre-built-`userManager` variant omits `userStore`.)
 */
export type OidcConfig = UserManagerSettings & { onSigninCallback: () => void };

export function buildOidcConfig(cfg: typeof appConfig = appConfig): OidcConfig {
  return {
    authority: cfg.oidc.issuer,
    client_id: cfg.oidc.clientId,
    redirect_uri: cfg.oidc.redirectUri,
    post_logout_redirect_uri: cfg.oidc.postLogoutRedirectUri,
    response_type: "code",
    scope: cfg.oidc.scope,

    // sessionStorage only — cleared on tab close (PRD M5 security requirement).
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),

    // Refresh the access token via the refresh token before it expires.
    automaticSilentRenew: true,

    // After the code exchange, rewrite the URL to the app root: this both strips
    // the OIDC query params (code/state/session_state) from history AND moves the
    // user off the /callback path onto a real route the client router renders.
    onSigninCallback: (): void => {
      window.history.replaceState({}, document.title, "/");
    },
  };
}
